/**
 * Session tracking utility for login/logout logging
 * Supports multiple concurrent sessions per user
 * Sessions are persisted in localStorage to survive page refreshes
 */

interface SessionInfo {
  sessionId: string;
  loginTime: number; // Unix timestamp in milliseconds
  userId?: string;
}

// Storage key prefix for localStorage
const SESSION_STORAGE_PREFIX = 'itsm_user_session_';

/**
 * Get sessions from localStorage for a specific user
 */
function getStoredSessions(userId: string): SessionInfo[] {
  try {
    const stored = localStorage.getItem(`${SESSION_STORAGE_PREFIX}${userId}`);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error reading sessions from localStorage:', error);
    return [];
  }
}

/**
 * Save sessions to localStorage for a specific user
 */
function saveStoredSessions(userId: string, sessions: SessionInfo[]): void {
  try {
    if (sessions.length === 0) {
      localStorage.removeItem(`${SESSION_STORAGE_PREFIX}${userId}`);
    } else {
      localStorage.setItem(`${SESSION_STORAGE_PREFIX}${userId}`, JSON.stringify(sessions));
    }
  } catch (error) {
    console.error('Error saving sessions to localStorage:', error);
  }
}

/**
 * Generate a unique session ID
 * Format: timestamp-randomUUID
 */
function getSecureRandomHex(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function generateSessionId(): string {
  const timestamp = Date.now();
  const random =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : getSecureRandomHex(16);
  return `${timestamp}-${random}`;
}

/**
 * Create a new session and store it
 * @param userId - User ID or email
 * @returns Session ID
 */
export function createSession(userId: string): string {
  const sessionId = generateSessionId();
  const loginTime = Date.now();

  const sessionInfo: SessionInfo = {
    sessionId,
    loginTime,
    userId,
  };

  // Get existing sessions from localStorage
  const userSessions = getStoredSessions(userId);
  userSessions.push(sessionInfo);
  
  // Save back to localStorage
  saveStoredSessions(userId, userSessions);

  return sessionId;
}

/**
 * Get session information
 * @param userId - User ID or email
 * @param sessionId - Session ID (optional, if not provided, returns most recent session)
 * @returns Session info or null if not found
 */
export function getSession(userId: string, sessionId?: string): SessionInfo | null {
  const userSessions = getStoredSessions(userId);
  if (!userSessions || userSessions.length === 0) {
    return null;
  }

  if (sessionId) {
    // Find specific session
    return userSessions.find(s => s.sessionId === sessionId) || null;
  } else {
    // Return most recent session (last in array, sorted by loginTime)
    const sortedSessions = [...userSessions].sort((a, b) => b.loginTime - a.loginTime);
    return sortedSessions[0] || null;
  }
}

/**
 * Remove a session and calculate active time
 * @param userId - User ID or email
 * @param sessionId - Session ID (optional, if not provided, removes most recent session)
 * @returns Object with sessionId and activeTime in milliseconds, or null if session not found
 */
export function removeSession(userId: string, sessionId?: string): { sessionId: string; activeTime: number } | null {
  const userSessions = getStoredSessions(userId);
  if (!userSessions || userSessions.length === 0) {
    return null;
  }

  let sessionToRemove: SessionInfo | null = null;
  let sessionIndex = -1;

  if (sessionId) {
    // Find specific session
    sessionIndex = userSessions.findIndex(s => s.sessionId === sessionId);
    if (sessionIndex !== -1) {
      sessionToRemove = userSessions[sessionIndex];
    }
  } else {
    // Remove most recent session (sorted by loginTime, most recent first)
    const sortedSessions = [...userSessions].sort((a, b) => b.loginTime - a.loginTime);
    sessionToRemove = sortedSessions[0];
    if (sessionToRemove) {
      sessionIndex = userSessions.findIndex(s => s.sessionId === sessionToRemove!.sessionId);
    }
  }

  if (!sessionToRemove || sessionIndex === -1) {
    return null;
  }

  // Calculate active time
  const logoutTime = Date.now();
  const activeTime = logoutTime - sessionToRemove.loginTime;

  // Remove session from array
  userSessions.splice(sessionIndex, 1);

  // Save back to localStorage
  saveStoredSessions(userId, userSessions);

  return {
    sessionId: sessionToRemove.sessionId,
    activeTime,
  };
}

/**
 * Get all active sessions for a user
 * @param userId - User ID or email
 * @returns Array of session IDs
 */
export function getActiveSessions(userId: string): string[] {
  const userSessions = getStoredSessions(userId);
  return userSessions ? userSessions.map(s => s.sessionId) : [];
}

/**
 * Clear all sessions for a user (useful for cleanup)
 * @param userId - User ID or email
 */
export function clearAllSessions(userId: string): void {
  localStorage.removeItem(`${SESSION_STORAGE_PREFIX}${userId}`);
}

/**
 * Clear all sessions for all users (useful for complete cleanup)
 */
export function clearAllUserSessions(): void {
  // Remove all session storage keys
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(SESSION_STORAGE_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
}

